// Mock data for documentation demo components

export interface ParsedResume {
  id: string;
  application_id: string;
  parsing_status: string;
  extracted_name?: string | null;
  extracted_email?: string | null;
  extracted_phone?: string | null;
  extracted_location?: string | null;
  skills: string[];
  experience: {
    title: string;
    company: string;
    start_date: string;
    end_date: string;
    duration_months: number;
    highlights: string[];
  }[];
  education: {
    institution: string;
    degree: string;
    field: string;
    graduation_year: string;
  }[];
  certifications?: string[];
  languages?: { language: string; proficiency: string }[];
  total_years_experience?: number;
  highest_degree?: string;
  ai_summary?: string;
  job_match_score?: number | null;
  match_analysis?: string | null;
  strengths?: string[];
  concerns?: string[];
  hiring_recommendation?: string;
  career_trajectory?: string;
  leadership_indicators?: string[];
  salary_expectation_range?: string | null;
  raw_text?: string | null;
  parsed_at?: string;
  parsing_error?: string | null;
  created_at: string;
}

export const mockCandidate = {
  id: "demo-candidate-1",
  full_name: "Sarah Chen",
  email: "sarah.chen@example.com",
  job_id: "demo-job-1",
  resume_url: "#",
  linkedin_url: "https://linkedin.com/in/example",
  consent_given: true,
  status: "active",
  stage_key: "shortlisted",
  stage_id: "demo-stage-2",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  ai_resume_score: 85,
  ai_interview_score: 78,
  ai_interview_status: "completed",
  ai_rank: 1,
  applicant_city: "San Francisco",
  applicant_country: "United States",
  applicant_country_code: "US",
  applicant_region: "California",
  applicant_timezone: "America/Los_Angeles",
  screening_answers: null,
  ai_interview_feedback: null,
  workstyle_status: "completed",
};

export const mockCandidates = [
  mockCandidate,
  {
    ...mockCandidate,
    id: "demo-candidate-2",
    full_name: "Michael Park",
    email: "m.park@example.com",
    ai_resume_score: 72,
    ai_interview_score: 65,
    ai_rank: 2,
    applicant_city: "New York",
  },
  {
    ...mockCandidate,
    id: "demo-candidate-3",
    full_name: "Emily Rodriguez",
    email: "emily.r@example.com",
    ai_resume_score: 91,
    ai_interview_score: null,
    ai_interview_status: "pending",
    ai_rank: null,
    applicant_city: "Austin",
  },
];

export const mockParsedResume: ParsedResume = {
  id: "demo-resume-1",
  application_id: "demo-candidate-1",
  parsing_status: "completed",
  extracted_name: "Sarah Chen",
  extracted_email: "sarah.chen@example.com",
  extracted_phone: "+1 (555) 123-4567",
  extracted_location: "San Francisco, CA",
  skills: ["React", "TypeScript", "Node.js", "PostgreSQL", "AWS", "GraphQL", "Docker", "CI/CD"],
  experience: [
    {
      title: "Senior Software Engineer",
      company: "TechCorp Inc.",
      start_date: "2021-01",
      end_date: "Present",
      duration_months: 36,
      highlights: [
        "Led development of customer-facing React applications serving 2M+ users",
        "Mentored team of 3 junior developers",
        "Reduced bundle size by 40% through code splitting"
      ]
    },
    {
      title: "Software Engineer",
      company: "StartupXYZ",
      start_date: "2019-03",
      end_date: "2021-01",
      duration_months: 22,
      highlights: [
        "Built full-stack features using Node.js and React",
        "Improved API performance by 40%"
      ]
    },
    {
      title: "Junior Developer",
      company: "WebAgency",
      start_date: "2017-06",
      end_date: "2019-02",
      duration_months: 20,
      highlights: [
        "Developed responsive web applications for various clients"
      ]
    }
  ],
  education: [
    {
      institution: "Stanford University",
      degree: "Bachelor of Science",
      field: "Computer Science",
      graduation_year: "2017"
    }
  ],
  certifications: ["AWS Solutions Architect", "Certified Kubernetes Administrator"],
  languages: [
    { language: "English", proficiency: "Native" },
    { language: "Mandarin", proficiency: "Fluent" }
  ],
  total_years_experience: 7,
  highest_degree: "Bachelor's",
  ai_summary: "Highly experienced full-stack developer with strong React and TypeScript expertise. Demonstrated ability to lead projects and mentor junior developers. Strong track record of improving system performance and delivering user-centric solutions.",
  job_match_score: 85,
  match_analysis: "Excellent match for senior frontend role. Has required React/TypeScript experience plus valuable backend knowledge. Leadership experience aligns with team lead responsibilities.",
  strengths: [
    "7+ years of relevant experience",
    "Strong React and TypeScript expertise",
    "Leadership and mentoring experience",
    "Track record of performance optimization"
  ],
  concerns: [
    "No direct experience with our specific tech stack (Vue.js)",
    "May be overqualified for mid-level position"
  ],
  hiring_recommendation: "Strongly Recommend",
  career_trajectory: "Upward",
  leadership_indicators: ["Led team of 5 engineers", "Mentored 3 junior developers"],
  salary_expectation_range: null,
  raw_text: null,
  parsed_at: new Date().toISOString(),
  parsing_error: null,
  created_at: new Date().toISOString(),
};

export const mockPipelineStages = [
  { id: "stage-1", key: "new", name: "New Applicants", position: 0 },
  { id: "stage-2", key: "shortlisted", name: "Shortlisted", position: 1 },
  { id: "stage-3", key: "ai_invited", name: "AI Interview", position: 2 },
  { id: "stage-4", key: "ranked", name: "Ranked", position: 3 },
  { id: "stage-5", key: "f2f", name: "Face to Face", position: 4 },
];

export const mockPipelineFunnelData = [
  { stage: "new", count: 45 },
  { stage: "shortlisted", count: 32 },
  { stage: "ai_invited", count: 18 },
  { stage: "ranked", count: 12 },
  { stage: "f2f", count: 5 },
];

export const mockInterviewScores = {
  overall: 78,
  clarity: 82,
  relevance: 75,
  depthOfKnowledge: 68,
};

export const mockWorkstyleScores = {
  conscientiousness: 78,
  communication: 85,
  collaboration: 72,
  adaptability: 80,
  ownership: 88,
  resilience: 65,
};

export const mockJob = {
  id: "demo-job-1",
  title: "Senior Frontend Engineer",
  location: "San Francisco, CA",
  location_type: "hybrid",
  employment_type: "full-time",
  status: "published",
  description: "We're looking for a Senior Frontend Engineer to join our growing team...",
  requirements: "5+ years of React experience, TypeScript proficiency...",
  responsibilities: "Lead frontend architecture decisions, mentor junior developers...",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export const mockDashboardStats = {
  totalJobs: 12,
  totalCandidates: 247,
  interviewsCompleted: 89,
  avgResumeScore: 72,
};
